import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type {
  ButtonHTMLAttributes,
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { cva } from "class-variance-authority";
import { Check, ChevronDown, Eye, EyeOff, LoaderCircle, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { BrandWordmark } from "../foundations/BrandWordmark";
import { cn } from "./lib/cn";
import "./styles/index.css";

function classes(...values: Array<string | false | null | undefined>) {
  return cn(values);
}

const PortalContext = createContext<HTMLElement | null>(null);

export function UiProvider({ children }: { readonly children: ReactNode }) {
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  return (
    <PortalContext.Provider value={portalContainer}>
      <div className="cpv2-root" data-carepass-ui="v2">
        {children}
        <div className="cpv2-portal" ref={setPortalContainer} />
      </div>
    </PortalContext.Provider>
  );
}

const buttonVariants = cva("cpv2-button", {
  variants: {
    variant: {
      primary: "cpv2-button--primary",
      secondary: "cpv2-button--secondary",
      quiet: "cpv2-button--quiet",
      danger: "cpv2-button--danger",
    },
    size: {
      small: "cpv2-button--small",
      medium: "cpv2-button--medium",
      large: "cpv2-button--large",
    },
  },
  defaultVariants: { variant: "primary", size: "medium" },
});

export function buttonClassName({
  variant = "primary",
  size = "medium",
  className,
}: {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly className?: string;
} = {}) {
  return classes(buttonVariants({ variant, size }), className);
}

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";
export type ButtonSize = "small" | "medium" | "large";
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly isLoading?: boolean;
  readonly loadingLabel?: string;
  readonly leadingIcon?: ReactNode;
  readonly trailingIcon?: ReactNode;
  readonly iconOnly?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "medium",
    isLoading = false,
    loadingLabel = "처리 중",
    leadingIcon,
    trailingIcon,
    iconOnly = false,
    className,
    disabled,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      className={classes(buttonClassName({ variant, size }), iconOnly && "cpv2-button--icon", className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      aria-label={isLoading ? loadingLabel : props["aria-label"]}
      data-loading={isLoading || undefined}
      data-slot="button"
    >
      {isLoading ? <LoaderCircle aria-hidden="true" className="cpv2-spin" size={16} /> : leadingIcon}
      {!iconOnly && <span>{children}</span>}
      {iconOnly && !isLoading ? children : null}
      {!isLoading ? trailingIcon : null}
    </button>
  );
});

interface FieldBaseProps {
  readonly id?: string;
  readonly label: string;
  readonly description?: string;
  readonly error?: string;
  readonly success?: string;
  readonly required?: boolean;
  readonly compact?: boolean;
  readonly leadingAdornment?: ReactNode;
  readonly trailingAdornment?: ReactNode;
  readonly format?: "phone";
  readonly className?: string;
}
type InputFieldProps = FieldBaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, keyof FieldBaseProps | "children"> & { readonly control?: "input" };
type TextareaFieldProps = FieldBaseProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, keyof FieldBaseProps> & { readonly control: "textarea" };
type SelectFieldProps = FieldBaseProps & Omit<SelectHTMLAttributes<HTMLSelectElement>, keyof FieldBaseProps> & { readonly control: "select"; readonly children: ReactNode };
export type FieldProps = InputFieldProps | TextareaFieldProps | SelectFieldProps;
export type FieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function formatPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export const Field = forwardRef<FieldElement, FieldProps>(function Field(
  {
    id: providedId,
    label,
    description,
    error,
    success,
    required = false,
    compact = false,
    leadingAdornment,
    trailingAdornment,
    format,
    className,
    control = "input",
    ...controlProps
  },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? `cpv2-field-${generatedId}`;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const descriptionId = description ? `${id}-description` : undefined;
  const feedbackId = error || success ? `${id}-feedback` : undefined;
  const describedBy = [descriptionId, feedbackId, controlProps["aria-describedby"]].filter(Boolean).join(" ") || undefined;
  const commonProps = {
    ...controlProps,
    id,
    required,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : undefined,
    className: "cpv2-field__control",
  };
  const rawInputProps = commonProps as InputHTMLAttributes<HTMLInputElement>;
  const isPassword = control === "input" && rawInputProps.type === "password";
  const formatsPhone = control === "input" && rawInputProps.type === "tel" && format === "phone";
  const inputProps = formatsPhone ? {
    ...rawInputProps,
    value: typeof rawInputProps.value === "string" || typeof rawInputProps.value === "number" ? formatPhoneNumber(String(rawInputProps.value)) : rawInputProps.value,
    defaultValue: typeof rawInputProps.defaultValue === "string" || typeof rawInputProps.defaultValue === "number" ? formatPhoneNumber(String(rawInputProps.defaultValue)) : rawInputProps.defaultValue,
    inputMode: rawInputProps.inputMode ?? "numeric",
    maxLength: rawInputProps.maxLength ?? 13,
    placeholder: rawInputProps.placeholder ?? "010-0000-0000",
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      event.currentTarget.value = formatPhoneNumber(event.currentTarget.value);
      rawInputProps.onChange?.(event);
    },
  } : rawInputProps;

  useEffect(() => {
    if (!isPassword || !passwordVisible) return;
    const hide = () => setPasswordVisible(false);
    const hideWhenBackgrounded = () => { if (document.hidden) hide(); };
    const form = document.getElementById(id)?.closest("form");
    form?.addEventListener("submit", hide);
    form?.addEventListener("reset", hide);
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", hideWhenBackgrounded);
    return () => {
      form?.removeEventListener("submit", hide);
      form?.removeEventListener("reset", hide);
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", hideWhenBackgrounded);
    };
  }, [id, isPassword, passwordVisible]);

  return (
    <div className={classes("cpv2-field", compact && "cpv2-field--compact", className)} data-state={error ? "error" : success ? "success" : "default"}>
      <label className="cpv2-field__label" htmlFor={id}>{label}{required ? <span aria-hidden="true" className="cpv2-field__required">필수</span> : null}</label>
      {description ? <span className="cpv2-field__description" id={descriptionId}>{description}</span> : null}
      <div className="cpv2-field__shell" onBlur={isPassword ? (event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPasswordVisible(false); } : undefined}>
        {leadingAdornment ? <span aria-hidden="true" className="cpv2-field__adornment">{leadingAdornment}</span> : null}
        {control === "textarea" ? (
          <textarea {...(commonProps as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} />
        ) : control === "select" ? (
          <select {...(commonProps as SelectHTMLAttributes<HTMLSelectElement>)} ref={ref as Ref<HTMLSelectElement>}>{(controlProps as SelectFieldProps).children}</select>
        ) : (
          <input {...inputProps} ref={ref as Ref<HTMLInputElement>} type={isPassword && passwordVisible && !inputProps.disabled ? "text" : inputProps.type} />
        )}
        {control === "select" && !trailingAdornment ? <ChevronDown aria-hidden="true" className="cpv2-field__select-icon" size={16} /> : null}
        {trailingAdornment ? <span aria-hidden="true" className="cpv2-field__adornment">{trailingAdornment}</span> : null}
        {isPassword ? (
          <button
            type="button"
            className="cpv2-field__toggle"
            aria-controls={id}
            aria-label={`${label} ${passwordVisible ? "숨기기" : "보기"}`}
            disabled={inputProps.disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setPasswordVisible((value) => !value)}
          >
            {passwordVisible ? <EyeOff aria-hidden="true" size={18} /> : <Eye aria-hidden="true" size={18} />}
          </button>
        ) : null}
      </div>
      {error || success ? <span className="cpv2-field__feedback" id={feedbackId} role={error ? "alert" : "status"}>{error ?? success}</span> : null}
    </div>
  );
});

export interface ChoiceControlProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  readonly label: ReactNode;
  readonly description?: string;
  readonly error?: string;
  readonly type?: "checkbox" | "radio";
}

export const ChoiceControl = forwardRef<HTMLInputElement, ChoiceControlProps>(function ChoiceControl(
  { label, description, error, type = "checkbox", className, ...props },
  ref,
) {
  const id = props.id ?? `cpv2-choice-${useId()}`;
  return (
    <div className={classes("cpv2-choice", className)} data-state={error ? "error" : undefined}>
      <label htmlFor={id}>
        <input {...props} id={id} ref={ref} type={type} />
        <span className="cpv2-choice__indicator" aria-hidden="true"><Check size={14} /></span>
        <span className="cpv2-choice__copy"><span>{label}</span>{description ? <small>{description}</small> : null}</span>
      </label>
      {error ? <p role="alert">{error}</p> : null}
    </div>
  );
});

export type StatusBadgeVariant = "information" | "waiting" | "processing" | "verification" | "attention" | "success" | "danger" | "neutral";
export function StatusBadge({ variant = "neutral", children }: { readonly variant?: StatusBadgeVariant; readonly children: ReactNode }) {
  return <span className={`cpv2-badge cpv2-badge--${variant}`} data-slot="badge"><span aria-hidden="true" />{children}</span>;
}

export interface AlertProps {
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
  readonly variant?: "information" | "success" | "warning" | "danger";
  readonly persistent?: boolean;
}
export function Alert({ title, children, action, variant = "information" }: AlertProps) {
  return (
    <div className={`cpv2-alert cpv2-alert--${variant}`} role={variant === "danger" || variant === "warning" ? "alert" : "status"}>
      <div><strong>{title}</strong>{children ? <div>{children}</div> : null}</div>{action ? <div>{action}</div> : null}
    </div>
  );
}

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly size?: "small" | "medium" | "large";
  readonly tone?: "default" | "danger";
  readonly children?: ReactNode;
  readonly primaryAction?: ReactNode;
  readonly secondaryAction?: ReactNode;
  readonly onClose: () => void;
  readonly closeLabel?: string;
  readonly closeDisabled?: boolean;
  readonly className?: string;
}
export function Dialog({ open, title, description, size = "small", tone = "default", children, primaryAction, secondaryAction, onClose, closeLabel = "대화상자 닫기", closeDisabled = false, className }: DialogProps) {
  const portalContainer = useContext(PortalContext);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next && !closeDisabled) onClose(); }}>
      {open ? (
        <DialogPrimitive.Portal container={portalContainer ?? undefined}>
          <DialogPrimitive.Overlay className="cpv2-dialog__overlay" />
          <DialogPrimitive.Content
            className={classes("cpv2-dialog", `cpv2-dialog--${size}`, `cpv2-dialog--${tone}`, className)}
            onEscapeKeyDown={(event) => { if (closeDisabled) event.preventDefault(); }}
            onPointerDownOutside={(event) => { if (closeDisabled) event.preventDefault(); }}
            onOpenAutoFocus={(event) => {
              returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
              event.preventDefault();
              titleRef.current?.focus();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              returnFocusRef.current?.focus();
              returnFocusRef.current = null;
            }}
          >
            <div className="cpv2-dialog__header">
              <div><DialogPrimitive.Title ref={titleRef} tabIndex={-1}>{title}</DialogPrimitive.Title>{description ? <DialogPrimitive.Description>{description}</DialogPrimitive.Description> : null}</div>
              <DialogPrimitive.Close asChild><Button iconOnly size="small" variant="quiet" aria-label={closeLabel} disabled={closeDisabled}><X aria-hidden="true" size={18} /></Button></DialogPrimitive.Close>
            </div>
            {children ? <div className="cpv2-dialog__body">{children}</div> : null}
            {secondaryAction || primaryAction ? <div className="cpv2-dialog__actions">{secondaryAction}{primaryAction}</div> : null}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      ) : null}
    </DialogPrimitive.Root>
  );
}

export interface DataColumn<T> { readonly key: string; readonly title: string; readonly numeric?: boolean; readonly sticky?: "start"; readonly render: (row: T) => ReactNode }
export interface DataTableProps<T> {
  readonly caption: string;
  readonly columns: readonly DataColumn<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string | number;
  readonly state?: "ready" | "loading" | "error" | "partial";
  readonly compact?: boolean;
  readonly onRetry?: () => void;
  readonly getRowState?: (row: T) => "preview-open" | "recently-viewed" | undefined;
}
export function DataTable<T>({ caption, columns, rows, rowKey, state = "ready", compact = false, onRetry, getRowState }: DataTableProps<T>) {
  return (
    <div className="cpv2-table-region" data-review-scroll-region role="region" aria-label={`${caption} 가로 탐색`} tabIndex={0} aria-busy={state === "loading" || undefined}>
      <table className={classes("cpv2-table", compact && "cpv2-table--compact")}>
        <caption data-admin-results-heading tabIndex={-1}>{caption}</caption>
        <thead><tr>{columns.map((column) => <th key={column.key} scope="col" data-numeric={column.numeric || undefined} data-sticky={column.sticky}>{column.title}</th>)}</tr></thead>
        <tbody>
          {state === "loading" ? <tr><td colSpan={columns.length}><div className="cpv2-table__state" role="status">목록을 불러오는 중입니다.</div></td></tr> : null}
          {state === "error" ? <tr><td colSpan={columns.length}><div className="cpv2-table__state"><Alert variant="danger" title="목록을 불러오지 못했습니다" action={onRetry ? <Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button> : undefined}>잠시 후 다시 조회해 주세요.</Alert></div></td></tr> : null}
          {state === "ready" && rows.length === 0 ? <tr><td colSpan={columns.length}><div className="cpv2-table__state">조건에 맞는 결과가 없습니다. 검색 조건을 변경해 주세요.</div></td></tr> : null}
          {(state === "ready" || state === "partial") ? rows.map((row) => <tr key={rowKey(row)} data-row-state={getRowState?.(row)}>{columns.map((column) => <td key={column.key} data-numeric={column.numeric || undefined} data-sticky={column.sticky}>{column.render(row)}</td>)}</tr>) : null}
        </tbody>
      </table>
      {state === "partial" ? <Alert variant="warning" title="일부 정보를 불러오지 못했습니다" action={onRetry ? <Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button> : undefined}>현재 확인된 결과만 표시합니다.</Alert> : null}
    </div>
  );
}

export function Pagination({ page, size, totalCount, onPageChange }: { readonly page: number; readonly size: number; readonly totalCount: number; readonly onPageChange: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(totalCount / size));
  return <nav className="cpv2-pagination" aria-label="목록 페이지"><span data-numeric>총 {totalCount.toLocaleString("ko-KR")}건 · {page + 1} / {pages}페이지</span><div><Button size="small" variant="quiet" disabled={page === 0} onClick={() => onPageChange(page - 1)}>이전 페이지</Button><Button size="small" variant="quiet" disabled={page + 1 >= pages} onClick={() => onPageChange(page + 1)}>다음 페이지</Button></div></nav>;
}

export function DetailList({ items }: { readonly items: readonly { readonly label: string; readonly value: ReactNode }[] }) {
  return <dl className="cpv2-detail-list">{items.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.value ?? "—"}</dd></div>)}</dl>;
}

export function FormActions({ secondary, primary, description, className }: { readonly secondary?: ReactNode; readonly primary: ReactNode; readonly description?: string; readonly className?: string }) {
  return <div className={classes("cpv2-form-actions", className)}>{description ? <p>{description}</p> : null}<div>{secondary}{primary}</div></div>;
}

export function StepIndicator({ items, currentId, label }: { readonly items: readonly { readonly id: string; readonly label: string }[]; readonly currentId: string; readonly label: string }) {
  const current = Math.max(0, items.findIndex((item) => item.id === currentId));
  return <div className="cpv2-steps" aria-label={label}><div><span style={{ width: `${((current + 1) / items.length) * 100}%` }} /></div><p>{items[current]?.label ?? "현재 단계"} <span>{current + 1}/{items.length}</span></p></div>;
}

export { BrandWordmark };
